import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, LockKeyhole, X } from "lucide-react";
import "./checkout.css";

type Line = {
  product: { id: number; name: string; price: number | string };
  variant?: { id: number; size?: string; color?: string };
  quantity: number;
};
const money = (v: any) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(v || 0),
  );
async function api(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem("rosi_customer_token");
  const r = await fetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || "Não foi possível continuar");
  return data;
}

export default function CheckoutModal({
  open,
  lines,
  onClose,
  onSuccess,
}: {
  open: boolean;
  lines: Line[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [customer, setCustomer] = useState<any>(null),
    [mode, setMode] = useState<"login" | "register">("login"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [auth, setAuth] = useState({
      name: "",
      email: "",
      password: "",
      customerType: "retail",
    }),
    [address, setAddress] = useState({
      recipient: "",
      postalCode: "",
      street: "",
      number: "",
      complement: "",
      district: "",
      city: "",
      state: "",
    }),
    [order, setOrder] = useState<any>(null);
  useEffect(() => {
    if (open && localStorage.getItem("rosi_customer_token"))
      api("/api/commerce/store/account")
        .then((c) => {
          setCustomer(c);
          setAddress((a) => ({ ...a, recipient: c.name || "" }));
        })
        .catch(() => localStorage.removeItem("rosi_customer_token"));
  }, [open]);
  if (!open) return null;
  const total = lines.reduce(
    (s, l) => s + Number(l.product.price) * l.quantity,
    0,
  );
  async function authenticate() {
    setBusy(true);
    setError("");
    try {
      const result = await api(`/api/commerce/store/auth/${mode}`, {
        method: "POST",
        body: JSON.stringify(
          mode === "login"
            ? { email: auth.email, password: auth.password }
            : { ...auth, marketingConsent: true },
        ),
      });
      localStorage.setItem("rosi_customer_token", result.token);
      setCustomer(result.customer);
      setAddress((a) => ({
        ...a,
        recipient: result.customer.name || auth.name,
      }));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function checkout() {
    setBusy(true);
    setError("");
    try {
      const created = await api("/api/commerce/store/checkout", {
        method: "POST",
        body: JSON.stringify({
          items: lines.map((l) => ({
            catalogItemId: l.product.id,
            variantId: l.variant?.id || null,
            quantity: l.quantity,
          })),
          shippingAddress: address,
        }),
      });
      setOrder(created);
      const payment = await api(
        `/api/commerce/store/orders/${created.id}/mercado-pago`,
        { method: "POST" },
      );
      onSuccess();
      window.location.href = payment.checkoutUrl;
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="checkout-backdrop">
      <section className="checkout-modal">
        <header>
          <div>
            <small>COMPRA SEGURA</small>
            <h2>
              {order
                ? "Pedido criado"
                : customer
                  ? "Entrega e pagamento"
                  : "Entre para continuar"}
            </h2>
          </div>
          <button onClick={onClose}>
            <X />
          </button>
        </header>
        {error && <div className="checkout-error">{error}</div>}
        {!customer ? (
          <div className="checkout-body">
            <div className="checkout-tabs">
              <button
                className={mode === "login" ? "active" : ""}
                onClick={() => setMode("login")}
              >
                Já tenho conta
              </button>
              <button
                className={mode === "register" ? "active" : ""}
                onClick={() => setMode("register")}
              >
                Criar conta
              </button>
            </div>
            {mode === "register" && (
              <>
                <label>
                  Nome completo
                  <input
                    value={auth.name}
                    onChange={(e) => setAuth({ ...auth, name: e.target.value })}
                  />
                </label>
                <label>
                  Tipo de cadastro
                  <select
                    value={auth.customerType}
                    onChange={(e) =>
                      setAuth({ ...auth, customerType: e.target.value })
                    }
                  >
                    <option value="retail">Cliente final</option>
                    <option value="reseller">Quero ser revendedora</option>
                  </select>
                </label>
              </>
            )}
            <label>
              E-mail
              <input
                type="email"
                value={auth.email}
                onChange={(e) => setAuth({ ...auth, email: e.target.value })}
              />
            </label>
            <label>
              Senha
              <input
                type="password"
                value={auth.password}
                onChange={(e) => setAuth({ ...auth, password: e.target.value })}
              />
            </label>
            <button
              className="checkout-primary"
              disabled={busy}
              onClick={authenticate}
            >
              {busy ? <Loader2 className="spin" /> : <LockKeyhole />}
              {mode === "login" ? "Entrar" : "Criar minha conta"}
            </button>
          </div>
        ) : (
          <div className="checkout-body">
            <p className="checkout-welcome">
              Olá, <b>{customer.name}</b>. Confirme onde deseja receber.
            </p>
            <div className="checkout-grid">
              <label className="wide">
                Destinatário
                <input
                  value={address.recipient}
                  onChange={(e) =>
                    setAddress({ ...address, recipient: e.target.value })
                  }
                />
              </label>
              <label>
                CEP
                <input
                  value={address.postalCode}
                  onChange={(e) =>
                    setAddress({ ...address, postalCode: e.target.value })
                  }
                />
              </label>
              <label>
                Estado
                <input
                  maxLength={2}
                  value={address.state}
                  onChange={(e) =>
                    setAddress({
                      ...address,
                      state: e.target.value.toUpperCase(),
                    })
                  }
                />
              </label>
              <label className="wide">
                Rua
                <input
                  value={address.street}
                  onChange={(e) =>
                    setAddress({ ...address, street: e.target.value })
                  }
                />
              </label>
              <label>
                Número
                <input
                  value={address.number}
                  onChange={(e) =>
                    setAddress({ ...address, number: e.target.value })
                  }
                />
              </label>
              <label>
                Complemento
                <input
                  value={address.complement}
                  onChange={(e) =>
                    setAddress({ ...address, complement: e.target.value })
                  }
                />
              </label>
              <label>
                Bairro
                <input
                  value={address.district}
                  onChange={(e) =>
                    setAddress({ ...address, district: e.target.value })
                  }
                />
              </label>
              <label>
                Cidade
                <input
                  value={address.city}
                  onChange={(e) =>
                    setAddress({ ...address, city: e.target.value })
                  }
                />
              </label>
            </div>
            <div className="checkout-summary">
              {lines.map((l) => (
                <p key={l.product.id}>
                  <span>
                    {l.quantity}× {l.product.name}
                  </span>
                  <b>{money(Number(l.product.price) * l.quantity)}</b>
                </p>
              ))}
              <p className="total">
                <span>Total dos produtos</span>
                <b>{money(total)}</b>
              </p>
              <small>Frete e prazo serão confirmados antes da expedição.</small>
            </div>
            <button
              className="checkout-primary"
              disabled={
                busy ||
                !address.postalCode ||
                !address.street ||
                !address.number ||
                !address.city ||
                !address.state
              }
              onClick={checkout}
            >
              {busy ? <Loader2 className="spin" /> : <CheckCircle2 />}Ir para
              pagamento seguro
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
